import React, { useState, useEffect, useContext } from 'react';
import { useNavigate } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { Modal, Icon } from '@PetSureAustralia/petsure-react-ui-library/distLib/components';
import { SLICES } from '../../../constants/slices';
import { CSPUrlsMap } from '../../../routes/CSPUrlsMap';
import { PetSelection } from '../../../components/claims/newClaim/newClaimSteps/petSelection';
import { useActivePet } from '../../../hooks/activePet';
import { PaymentHistoryTable } from '../../../components/payments/paymentHistoryTable/paymentHistoryTable';
import { EnrichTransactionData } from '../../../helpers/transactionHelpers';
import { useGetPremiumPaymentMutation, useGetPetInfoQuery } from '../../../data/policy';
import './premiumsAndPayments.scss';
import Loading from '../../../components/shared/Loading/Loading';
import { FeatureFlagContext } from '../../../contextProviders/featureFlagContext';

export const PremiumsAndPayments = () => {
  const navigate = useNavigate();
  const { cspConfiguration, isCspConfigLoading } = useContext(FeatureFlagContext);
  const isPayNowEnabled = cspConfiguration?.IsPayNowEnabled;
  const [isPopupShowing, setPopupIsShowing] = useState(false);
  const { authDetails } = useSelector((state) => state?.[SLICES.AUTHENTICATION]);
  const { auth0AccessToken } = authDetails;
  // Using the RTK query hook for getting pet info (only triggered when token is available)
  const {
    data: petDetails,
    isLoading: petDetailsLoading,
    isError: petDetailsError
  } = useGetPetInfoQuery(auth0AccessToken, {
    skip: !auth0AccessToken
  });
  const [
    getPremiumPayment,
    { data: premiumPayments, isLoading: premiumPaymentsLoading, isSuccess: premiumPaymentsSuccess }
  ] = useGetPremiumPaymentMutation();

  const [enrichedTransactionsData, setEnrichedTransactionsData] = useState();

  useEffect(() => {
    if (petDetails && premiumPayments) {
      const data = EnrichTransactionData({ petDetails, transactionsData: premiumPayments });
      setEnrichedTransactionsData(data);
    }
  }, [petDetails, premiumPayments]);

  useEffect(() => {
    if (petDetails && petDetails.length > 0 && auth0AccessToken) {
      const policies = petDetails.map((pet) => ({
        PolicyNumber: pet.PolicyNumber,
        PetName: pet.Name,
        Status: pet.PolicyDetails.Status,
        PolicyStartDate: new Date(pet.PolicyDetails.PolicyStartDate)
      }));

      // Find the oldest PolicyStartDate
      const FilterDateFrom = new Date(Math.min(...policies.map((pet) => pet.PolicyStartDate)));

      // Get today's date for FilterDateTo
      const FilterDateTo = new Date();

      // Construct the payload object
      const requestPayload = {
        Policies: policies,
        FilterDateFrom,
        FilterDateTo
      };

      // Call your getPremiumPayment function with the structured payload
      getPremiumPayment({ data: requestPayload, auth0Token: auth0AccessToken });
    }
  }, [petDetails, getPremiumPayment, auth0AccessToken]);

  const { activePolicyId } = useActivePet({ petDetails });

  const [selectedPolicyId, setSelectedPolicyId] = useState(activePolicyId || '');

  const HandlePetSelection = (selection) => {
    setSelectedPolicyId(selection.policyNumber);
  };

  const HandlePolicyDetailsLink = () => {
    navigate(`${CSPUrlsMap.myPets.url}?policy=${selectedPolicyId}`);
  };

  useEffect(() => {
    if (isCspConfigLoading || typeof isPayNowEnabled !== 'boolean') return;
    setPopupIsShowing(!isPayNowEnabled);
  }, [isPayNowEnabled, isCspConfigLoading]);

  const onClose = () => setPopupIsShowing(false);
  const onBackToDashboard = () => navigate(CSPUrlsMap.dashboard.url);

  return (
    <main
      className="premiumsAndPayments"
      aria-label="Premiums and Payments page"
    >
      {isPopupShowing && (
        <Modal
          customCssClass="importantMessagePopup"
          size="small"
          isContentScrollable
          showFooterDivider
          showCloseIconButton={false}
          open
          onClose={onClose}
          header={
            <h2 className="modal-title">
              <Icon icon="warning" /> Important Message
            </h2>
          }
          footer={
            <button
              onClick={onBackToDashboard}
              type="button"
              className="button primary nextButton"
            >
              <span>Back to dashboard</span>
            </button>
          }
        >
          <section className="importantMessageSection">
            <b>Our Pay Now feature is currently unavailable.</b>
            <p>Thanks for your patience as we work to get it back online.</p>
            <p>
              Please try again later, or if you would like to make a payment now, contact our customer service team.
            </p>
          </section>
        </Modal>
      )}
      <h1 className="mainPageHeader">Premiums &amp; payments</h1>

      <h2>View premiums &amp; claim payments for</h2>
      {petDetailsLoading ? (
        <Loading />
      ) : (
        <PetSelection
          petDetails={petDetails}
          onPetSelection={HandlePetSelection}
          activePolicyNumber={selectedPolicyId ?? ''}
        />
      )}

      <h3>Premiums &amp; claim payments</h3>
      <p>
        This is for your current policy period only.
        <button
          type="button"
          onClick={HandlePolicyDetailsLink}
          className="button primary text_only"
        >
          <span className="labelText">Find out more</span>
          <span className="sr-only"> about your policy</span>
        </button>
      </p>
      {petDetailsLoading || premiumPaymentsLoading || !enrichedTransactionsData ? (
        <Loading />
      ) : (
        <PaymentHistoryTable
          petDetails={petDetails}
          tableId="paymentHistoryTable"
          transactionsData={enrichedTransactionsData}
          selectedPolicyId={selectedPolicyId}
          setSelectedPolicyId={setSelectedPolicyId}
        />
      )}
    </main>
  );
};
